import React, { useState, useEffect } from "react";
import { Coins, X, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * SetPriceButton – eier av innhold setter pris i tokens.
 * 0 = gratis. Ellers 10-500 tokens. Bilder/video krever selger-verifisering.
 *
 * Props:
 *  - contentType: "image" | "video" | "novelle"
 *  - contentId: string
 *  - currentPrice: number (current price_tokens, 0 if free)
 *  - onSaved?: (newPrice) => void
 *  - variant?: "chip" | "default"
 */
export default function SetPriceButton({ contentType, contentId, currentPrice = 0, onSaved, variant = "chip" }) {
  const [open, setOpen] = useState(false);

  const baseBtn = variant === "chip"
    ? "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-meta uppercase tracking-[0.12em] border border-[var(--gold)]/50 text-[var(--gold)] hover:bg-[var(--gold)] hover:text-black transition"
    : "inline-flex items-center gap-2 px-4 py-2 text-xs font-meta uppercase tracking-[0.15em] border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-black transition";

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={baseBtn}
        data-testid={`set-price-btn-${contentId}`}
      >
        <Coins className="h-3 w-3" />
        {currentPrice > 0 ? `${currentPrice} tok` : "Sett pris"}
      </button>
      {open && (
        <SetPriceModal
          contentType={contentType}
          contentId={contentId}
          currentPrice={currentPrice}
          onClose={() => setOpen(false)}
          onSaved={(newPrice) => {
            setOpen(false);
            if (onSaved) onSaved(newPrice);
          }}
        />
      )}
    </>
  );
}

function SetPriceModal({ contentType, contentId, currentPrice, onClose, onSaved }) {
  const [price, setPrice] = useState(currentPrice || 0);
  const [busy, setBusy] = useState(false);
  const isPaid = price > 0;
  const valid = price === 0 || (price >= 10 && price <= 500);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/content/set-price", {
        content_type: contentType,
        content_id: contentId,
        price: price || 0,
      });
      toast.success(price > 0 ? `Pris satt til ${price} tokens` : "Innholdet er nå gratis");
      onSaved(price);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "seller_verification_required") {
        toast.error("Du må selger-verifisere deg først (ID + selfie). Gå til Innstillinger → Selger-verifisering.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  const setFree = () => setPrice(0);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-md mx-4 bg-[var(--noir-elev)] border border-white/10 p-7" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[var(--cream-soft)] hover:text-[var(--cream)]" data-testid="set-price-close">
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center bg-[var(--gold)]/15 border border-[var(--gold)]/40">
            <Coins className="h-5 w-5 text-[var(--gold)]" />
          </div>
          <div>
            <div className="font-fraunces italic text-2xl text-[var(--cream)] leading-none">Sett pris</div>
            <div className="font-meta mt-1">
              {contentType === "novelle" ? "Novelle" : contentType === "video" ? "Video" : "Bilde"}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="font-meta mb-2">Pris i tokens (10–500)</div>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={0}
              max={500}
              value={price}
              onChange={(e) => setPrice(parseInt(e.target.value, 10) || 0)}
              className="input-editorial flex-1"
              data-testid="set-price-input"
            />
            <button onClick={setFree}
              className={`px-3 py-1.5 text-xs font-meta border transition ${
                price === 0 ? "border-[var(--magenta)] text-[var(--magenta)]" : "border-white/20 text-[var(--cream-soft)]"
              }`}>
              Gratis
            </button>
          </div>
          {price > 0 && price < 10 && (
            <div className="text-xs text-[var(--cream)] font-medium mt-2 border-l-2 border-[var(--magenta)] pl-3">
              Minste tillatte pris er 10 tokens (eller 0 for gratis).
            </div>
          )}
        </div>

        {isPaid && (
          <div className="border-l-2 border-[var(--gold)] bg-[var(--gold)]/5 pl-3 py-2 mb-5">
            <p className="font-manrope text-xs text-[var(--cream-soft)] leading-relaxed">
              Du mottar <strong className="text-[var(--gold)]">{Math.floor(price * 0.9)} tokens</strong> per kjøp
              ({Math.max(1, Math.floor(price * 0.1))} til plattform = 10%).
              {(contentType === "image" || contentType === "video") && " Bilder/video krever selger-verifisering."}
            </p>
          </div>
        )}

        <button onClick={save} disabled={!valid || busy} className="btn-magenta w-full py-2.5" data-testid="set-price-save">
          {busy ? "Lagrer…" : (price > 0 ? `Selg for ${price} tokens` : "Sett som gratis")}
        </button>
      </div>
    </div>
  );
}


/**
 * BuyContentButton – kjøper klikker for å kjøpe betalt innhold.
 *
 * Props:
 *  - contentType: "image" | "video" | "novelle"
 *  - contentId: string
 *  - price: number (tokens)
 *  - hasAccess: boolean (initial)
 *  - sellerUsername?: string
 *  - onPurchased?: () => void
 */
export function BuyContentButton({ contentType, contentId, price, hasAccess: initialAccess, sellerUsername, onPurchased }) {
  const [hasAccess, setHasAccess] = useState(initialAccess);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => { setHasAccess(initialAccess); }, [initialAccess]);

  if (hasAccess) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-meta uppercase tracking-[0.12em] bg-emerald-500/10 border border-emerald-500/40 text-emerald-300">
        <Check className="h-3 w-3" /> Kjøpt
      </span>
    );
  }

  const buy = async () => {
    setBusy(true);
    try {
      const r = await api.post("/content/buy", { content_type: contentType, content_id: contentId });
      toast.success(`✓ Tilgang gitt! Saldo: ${r.data.new_balance ?? "?"} tokens`);
      setHasAccess(true);
      setConfirm(false);
      if (onPurchased) onPurchased();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirm(true); }}
        className="inline-flex items-center gap-2 px-4 py-2 text-xs font-meta uppercase tracking-[0.15em] bg-[var(--magenta)] text-black hover:bg-[var(--magenta)]/85 transition"
        data-testid={`buy-content-btn-${contentId}`}
      >
        <Coins className="h-3.5 w-3.5" /> Kjøp for {price} tokens
      </button>

      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setConfirm(false)}>
          <div className="relative w-full max-w-sm mx-4 bg-[var(--noir-elev)] border border-white/10 p-7" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setConfirm(false)} className="absolute top-3 right-3 text-[var(--cream-soft)] hover:text-[var(--cream)]">
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-[var(--magenta)]/15 border border-[var(--magenta)]/40">
                <Lock className="h-5 w-5 text-[var(--magenta)]" />
              </div>
              <div>
                <div className="font-fraunces italic text-2xl text-[var(--cream)] leading-none">Bekreft kjøp</div>
                <div className="font-meta mt-1">{price} tokens</div>
              </div>
            </div>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mb-5 leading-relaxed">
              Du kjøper varig tilgang til {contentType === "novelle" ? "denne novellen" : "dette innholdet"}
              {sellerUsername && <> fra <strong className="text-[var(--cream)]">@{sellerUsername}</strong></>}.
              Beløpet trekkes fra din token-saldo umiddelbart. Kjøp kan ikke refunderes.
            </p>
            <div className="flex gap-2">
              <button onClick={buy} disabled={busy} className="btn-magenta flex-1 py-2.5" data-testid="confirm-buy-btn">
                {busy ? "Behandler…" : `Bekreft – ${price} tokens`}
              </button>
              <button onClick={() => setConfirm(false)} className="btn-ghost px-4">Avbryt</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
